// ---------------------------------------------------------------------------
// Data Relationship Graph — 4-column diagram: datasources -> flows -> state -> UI.
//
// Renders in the Data mode's Graph view. Uses @xyflow/react with its own
// ReactFlowProvider. Clicking a node traces the full connected path upstream
// and downstream; all unrelated nodes dim to 20% opacity. Node click syncs
// with DataInspector via studioStore.selectEntity().
// ---------------------------------------------------------------------------

import { useCallback, useMemo, useState } from 'react'
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  BackgroundVariant,
  Handle,
  Position,
  type Node,
  type Edge,
  type NodeProps,
  type Connection,
} from '@xyflow/react'
import { Database, Zap, Variable, Component } from 'lucide-react'
import { useAppOntology } from '@/features/flow-editor/hooks/use-app-ontology'
import { useStudioStore } from '@/store/studio-store'
import { CANVAS } from '@/features/flow-editor/lib/design-tokens'
import type { AppOntology, OntologyEdge } from '@/features/flow-editor/services/app-ontology'
import { RelationshipDetailPanel } from './relationship-detail-panel'

// ---------------------------------------------------------------------------
// Layout constants
// ---------------------------------------------------------------------------

const COL_X = { datasource: 0, flow: 280, state: 560, uiBinding: 840 }
const ROW_GAP = 60
const BLOCK_GROUP_GAP = 40

// ---------------------------------------------------------------------------
// Edge styles per type
// ---------------------------------------------------------------------------

const EDGE_STYLES: Record<OntologyEdge['type'], { stroke: string; strokeDasharray?: string }> = {
  fetch:    { stroke: '#f59e0b' },
  write:    { stroke: 'hsl(var(--primary))' },
  read:     { stroke: '#3b82f6', strokeDasharray: '5,3' },
  bind:     { stroke: '#a855f7' },
  callFlow: { stroke: '#9ca3af', strokeDasharray: '5,3' },
  navigate: { stroke: '#a855f7', strokeDasharray: '5,3' },
}

// ---------------------------------------------------------------------------
// Custom Node Components
// ---------------------------------------------------------------------------

function DatasourceNode({ data }: NodeProps) {
  return (
    <div
      className="flex items-center gap-2 rounded-lg border bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800 px-3 py-2 shadow-sm min-w-[120px]"
      style={{ opacity: data.opacity as number ?? 1 }}
    >
      <Database size={14} className="text-amber-600 dark:text-amber-400 shrink-0" />
      <div className="flex flex-col min-w-0">
        <span className="text-xs font-medium text-amber-900 dark:text-amber-200 truncate">
          {String(data.label ?? '')}
        </span>
        {data.badge != null && data.badge !== '' && (
          <span className="text-[10px] text-amber-600 dark:text-amber-400 truncate">
            {String(data.badge)}
          </span>
        )}
      </div>
      <Handle type="source" position={Position.Right} className="!w-2 !h-2 !bg-amber-400" />
    </div>
  )
}

function FlowNode({ data }: NodeProps) {
  return (
    <div
      className="flex items-center gap-2 rounded-lg border bg-primary/10 dark:bg-primary/10 border-primary/20 dark:border-primary/30 px-3 py-2 shadow-sm min-w-[120px]"
      style={{ opacity: data.opacity as number ?? 1 }}
    >
      <Handle type="target" position={Position.Left} className="!w-2 !h-2 !bg-primary" />
      <Zap size={14} className="text-primary dark:text-primary shrink-0" />
      <div className="flex flex-col min-w-0">
        <span className="text-xs font-medium text-primary dark:text-primary truncate">
          {String(data.label ?? '')}
        </span>
        {data.badge != null && data.badge !== '' && (
          <span className="text-[10px] text-primary dark:text-primary truncate">
            {String(data.badge)}
          </span>
        )}
      </div>
      <Handle type="source" position={Position.Right} className="!w-2 !h-2 !bg-primary" />
    </div>
  )
}

function StateNode({ data }: NodeProps) {
  return (
    <div
      className="flex items-center gap-2 rounded-lg border bg-blue-50 dark:bg-blue-950/40 border-blue-200 dark:border-blue-800 px-3 py-2 shadow-sm min-w-[120px]"
      style={{ opacity: data.opacity as number ?? 1 }}
    >
      <Handle type="target" position={Position.Left} className="!w-2 !h-2 !bg-blue-400" />
      <Variable size={14} className="text-blue-600 dark:text-blue-400 shrink-0" />
      <div className="flex flex-col min-w-0">
        <span className="text-xs font-medium text-blue-900 dark:text-blue-200 truncate">
          {String(data.label ?? '')}
        </span>
        {data.badge != null && data.badge !== '' && (
          <span className="text-[10px] text-blue-600 dark:text-blue-400 truncate">
            {String(data.badge)}
          </span>
        )}
      </div>
      <Handle type="source" position={Position.Right} className="!w-2 !h-2 !bg-blue-400" />
    </div>
  )
}

function UIBindingNode({ data }: NodeProps) {
  return (
    <div
      className="flex items-center gap-2 rounded-lg border bg-purple-50 dark:bg-purple-950/40 border-purple-200 dark:border-purple-800 px-3 py-2 shadow-sm min-w-[120px]"
      style={{ opacity: data.opacity as number ?? 1 }}
    >
      <Handle type="target" position={Position.Left} className="!w-2 !h-2 !bg-purple-400" />
      <Component size={14} className="text-purple-600 dark:text-purple-400 shrink-0" />
      <div className="flex flex-col min-w-0">
        <span className="text-xs font-medium text-purple-900 dark:text-purple-200 truncate">
          {String(data.label ?? '')}
        </span>
        {data.badge != null && data.badge !== '' && (
          <span className="text-[10px] text-purple-600 dark:text-purple-400 truncate">
            {String(data.badge)}
          </span>
        )}
      </div>
    </div>
  )
}

const nodeTypes = {
  datasource: DatasourceNode,
  flowNode: FlowNode,
  stateNode: StateNode,
  uiBinding: UIBindingNode,
}

// ---------------------------------------------------------------------------
// Graph traversal — find connected nodes upstream + downstream
// ---------------------------------------------------------------------------

function findConnectedSet(
  selectedId: string,
  edges: OntologyEdge[],
): Set<string> {
  const connected = new Set<string>([selectedId])
  const edgesBySource = new Map<string, OntologyEdge[]>()
  const edgesByTarget = new Map<string, OntologyEdge[]>()

  for (const e of edges) {
    if (!edgesBySource.has(e.source)) edgesBySource.set(e.source, [])
    edgesBySource.get(e.source)!.push(e)
    if (!edgesByTarget.has(e.target)) edgesByTarget.set(e.target, [])
    edgesByTarget.get(e.target)!.push(e)
  }

  // BFS upstream
  const upQueue = [selectedId]
  while (upQueue.length > 0) {
    const current = upQueue.pop()!
    const incoming = edgesByTarget.get(current) ?? []
    for (const e of incoming) {
      if (!connected.has(e.source)) {
        connected.add(e.source)
        upQueue.push(e.source)
      }
    }
  }

  // BFS downstream
  const downQueue = [selectedId]
  while (downQueue.length > 0) {
    const current = downQueue.pop()!
    const outgoing = edgesBySource.get(current) ?? []
    for (const e of outgoing) {
      if (!connected.has(e.target)) {
        connected.add(e.target)
        downQueue.push(e.target)
      }
    }
  }

  return connected
}

// ---------------------------------------------------------------------------
// Layout algorithm — group by block, 4 columns
// ---------------------------------------------------------------------------

interface LayoutResult {
  nodes: Node[]
  edges: Edge[]
}

function layoutOntology(
  ontology: AppOntology,
  selectedNodeId: string | null,
): LayoutResult {
  const connectedSet = selectedNodeId
    ? findConnectedSet(selectedNodeId, ontology.edges)
    : null

  const opacityFor = (id: string): number => {
    if (!connectedSet) return 1
    return connectedSet.has(id) ? 1 : 0.2
  }

  // Collect all block names (excluding 'appData' pseudo-block)
  const blockNames = new Set<string>()
  for (const n of ontology.datasources) blockNames.add(n.blockName)
  for (const n of ontology.flows) blockNames.add(n.blockName)
  for (const n of ontology.states) {
    if (n.blockName !== 'appData') blockNames.add(n.blockName)
  }
  for (const n of ontology.uiBindings) blockNames.add(n.blockName)

  const sortedBlocks = [...blockNames].sort()

  // Y accumulators per column
  const colY: Record<string, number> = {
    datasource: 0,
    flow: 0,
    state: 0,
    uiBinding: 0,
  }

  const rfNodes: Node[] = []

  // --- Global appData state nodes first (top group) ---
  const appDataStates = ontology.states.filter((s) => s.scope === 'appData')
  if (appDataStates.length > 0) {
    for (const s of appDataStates) {
      rfNodes.push({
        id: s.id,
        type: 'stateNode',
        position: { x: COL_X.state, y: colY.state },
        data: {
          label: s.key,
          badge: 'appData',
          opacity: opacityFor(s.id),
          nodeType: 'state',
        },
      })
      colY.state += ROW_GAP
    }
    Object.keys(colY).forEach((k) => {
      colY[k] = Math.max(colY[k], colY.state + BLOCK_GROUP_GAP)
    })
  }

  // --- Per-block nodes ---
  for (const blockName of sortedBlocks) {
    const startY = Math.max(colY.datasource, colY.flow, colY.state, colY.uiBinding)

    // Datasources for this block
    const blockDs = ontology.datasources.filter((d) => d.blockName === blockName)
    colY.datasource = startY
    for (const ds of blockDs) {
      rfNodes.push({
        id: ds.id,
        type: 'datasource',
        position: { x: COL_X.datasource, y: colY.datasource },
        data: {
          label: `${ds.method} ${ds.url.length > 30 ? ds.url.slice(0, 30) + '...' : ds.url}`,
          badge: ds.method,
          opacity: opacityFor(ds.id),
          nodeType: 'datasource',
        },
      })
      colY.datasource += ROW_GAP
    }

    // Flows for this block
    const blockFlows = ontology.flows.filter((f) => f.blockName === blockName)
    colY.flow = startY
    for (const f of blockFlows) {
      rfNodes.push({
        id: f.id,
        type: 'flowNode',
        position: { x: COL_X.flow, y: colY.flow },
        data: {
          label: f.flowName,
          badge: f.trigger,
          opacity: opacityFor(f.id),
          nodeType: 'flow',
          flowName: f.flowName,
        },
      })
      colY.flow += ROW_GAP
    }

    // Local state for this block
    const blockStates = ontology.states.filter(
      (s) => s.blockName === blockName && s.scope === 'local',
    )
    colY.state = startY
    for (const s of blockStates) {
      rfNodes.push({
        id: s.id,
        type: 'stateNode',
        position: { x: COL_X.state, y: colY.state },
        data: {
          label: s.key,
          badge: 'local',
          opacity: opacityFor(s.id),
          nodeType: 'state',
        },
      })
      colY.state += ROW_GAP
    }

    // UI bindings for this block
    const blockBindings = ontology.uiBindings.filter((b) => b.blockName === blockName)
    colY.uiBinding = startY
    for (const b of blockBindings) {
      rfNodes.push({
        id: b.id,
        type: 'uiBinding',
        position: { x: COL_X.uiBinding, y: colY.uiBinding },
        data: {
          label: b.elementId,
          badge: b.boundVariable,
          opacity: opacityFor(b.id),
          nodeType: 'uiBinding',
        },
      })
      colY.uiBinding += ROW_GAP
    }

    // Block group gap
    const maxY = Math.max(colY.datasource, colY.flow, colY.state, colY.uiBinding)
    colY.datasource = maxY + BLOCK_GROUP_GAP
    colY.flow = maxY + BLOCK_GROUP_GAP
    colY.state = maxY + BLOCK_GROUP_GAP
    colY.uiBinding = maxY + BLOCK_GROUP_GAP
  }

  // --- Build edges ---
  const rfEdges: Edge[] = ontology.edges.map((e) => {
    const style = EDGE_STYLES[e.type] ?? { stroke: '#6b7280' }
    const isConnected = !connectedSet || (connectedSet.has(e.source) && connectedSet.has(e.target))

    return {
      id: e.id,
      source: e.source,
      target: e.target,
      label: e.label,
      style: {
        stroke: style.stroke,
        strokeWidth: isConnected && connectedSet ? 2.5 : 1.5,
        strokeDasharray: style.strokeDasharray,
        opacity: isConnected ? 1 : 0.1,
      },
      labelStyle: { fontSize: 10, fill: '#6b7280' },
      type: 'default',
    }
  })

  return { nodes: rfNodes, edges: rfEdges }
}

// ---------------------------------------------------------------------------
// parseOntologyId — structured parse of colon-delimited ontology IDs
// ---------------------------------------------------------------------------

export interface ParsedOntologyId {
  type: string
  blockName: string
  [key: string]: string
}

export function parseOntologyId(id: string): ParsedOntologyId {
  const parts = id.split(':')
  const prefix = parts[0]

  switch (prefix) {
    case 'ds':
      // "ds:{blockName}:{flowName}:{index}"
      return { type: 'datasource', blockName: parts[1] ?? '', flowName: parts[2] ?? '', index: parts[3] ?? '' }
    case 'state':
      // "state:{blockName}:{key}"
      return { type: 'state', blockName: parts[1] ?? '', key: parts.slice(2).join(':') }
    case 'flow':
      // "flow:{blockName}:{flowName}"
      return { type: 'flow', blockName: parts[1] ?? '', flowName: parts[2] ?? '' }
    case 'ui':
      // "ui:{blockName}:{elementId}:{varKey}"
      return { type: 'uiBinding', blockName: parts[1] ?? '', elementId: parts[2] ?? '', varKey: parts[3] ?? '' }
    case 'resp':
      // "resp:{blockName}:{dsName}:{fieldName}"
      return { type: 'responseField', blockName: parts[1] ?? '', dsName: parts[2] ?? '', fieldName: parts[3] ?? '' }
    default:
      return { type: 'unknown', blockName: parts[1] ?? '' }
  }
}

// ---------------------------------------------------------------------------
// isValidConnection — only datasource/responseField -> state allowed
// ---------------------------------------------------------------------------

export function isValidConnection(connection: { source: string | null; target: string | null }): boolean {
  if (!connection.source || !connection.target) return false
  const source = parseOntologyId(connection.source)
  const target = parseOntologyId(connection.target)
  return (source.type === 'datasource' || source.type === 'responseField') && target.type === 'state'
}

// ---------------------------------------------------------------------------
// Ontology ID -> Entity ID mapping (for DataInspector sync)
// ---------------------------------------------------------------------------

export function ontologyIdToEntityId(ontologyId: string): string | null {
  if (ontologyId.startsWith('state:')) {
    // "state:{blockName}:{key}" -> "{blockName}.variables.{key}"
    const parts = ontologyId.split(':')
    if (parts.length >= 3) {
      const blockName = parts[1]
      const key = parts.slice(2).join(':')
      return `${blockName}.variables.${key}`
    }
  } else if (ontologyId.startsWith('ds:')) {
    // "ds:{blockName}:{flowName}:{index}" -> "{blockName}.datasources.{flowName}"
    const parts = ontologyId.split(':')
    if (parts.length >= 3) {
      return `${parts[1]}.datasources.${parts[2]}`
    }
  } else if (ontologyId.startsWith('flow:')) {
    // "flow:{blockName}:{flowName}" -> "{blockName}.flows.{flowName}"
    const parts = ontologyId.split(':')
    if (parts.length >= 3) {
      return `${parts[1]}.flows.${parts[2]}`
    }
  } else if (ontologyId.startsWith('ui:')) {
    // "ui:{blockName}:{elementId}:{varKey}" -> "{blockName}.variables.{varKey}"
    const parts = ontologyId.split(':')
    if (parts.length >= 4) {
      return `${parts[1]}.variables.${parts[3]}`
    }
  }
  return null
}

// ---------------------------------------------------------------------------
// DataRelationshipGraphInner
// ---------------------------------------------------------------------------

function DataRelationshipGraphInner() {
  const ontology = useAppOntology()
  const selectEntity = useStudioStore((s) => s.selectEntity)

  // Ephemeral selection state (not in store — per research anti-pattern guidance)
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null)

  const { nodes, edges } = useMemo(
    () => layoutOntology(ontology, selectedNodeId),
    [ontology, selectedNodeId],
  )

  const onNodeClick = useCallback((_: React.MouseEvent, node: Node) => {
    setSelectedNodeId((prev) => {
      const next = prev === node.id ? null : node.id
      // Sync with DataInspector
      selectEntity(next ? ontologyIdToEntityId(next) : null)
      return next
    })
  }, [selectEntity])

  const onPaneClick = useCallback(() => {
    setSelectedNodeId(null)
    selectEntity(null)
  }, [selectEntity])

  // xyflow's IsValidConnection<Edge> = (edge: Edge | Connection) => boolean.
  // When dragging a new connection, ReactFlow passes a Connection; when validating
  // an existing edge, it passes an Edge. We only validate new drags, so narrow
  // to Connection via the `source`/`target` fields that both shapes share.
  const handleIsValidConnection = useCallback((edgeOrConnection: Edge | Connection): boolean => {
    return isValidConnection(edgeOrConnection as Connection)
  }, [])

  const onConnect = useCallback((connection: Connection) => {
    if (!connection.source || !connection.target) return
    const source = parseOntologyId(connection.source)
    const target = parseOntologyId(connection.target)
    if ((source.type !== 'datasource' && source.type !== 'responseField') || target.type !== 'state') return

    const store = useStudioStore.getState()
    const dsBlockName = source.blockName
    const dsName = source.type === 'responseField' ? source.dsName : source.flowName
    const stateKey = target.key

    // Check if target variable exists; if not, create it
    const block = store.currentProject?.blocks[target.blockName]
    const vars = block?.state as unknown as Record<string, Record<string, unknown>> | undefined
    const stateScope = 'state'
    if (!vars?.[stateScope]?.[stateKey]) {
      store.addVariable(target.blockName, stateScope, stateKey, { type: 'any', default: null })
    }

    // Set resultTarget on the datasource
    store.updateDatasource(dsBlockName, dsName, { resultTarget: stateKey })
  }, [])

  return (
    <div className="h-full w-full flex flex-col">
      <div className="flex-1 min-h-0">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          onNodeClick={onNodeClick}
          onPaneClick={onPaneClick}
          onConnect={onConnect}
          isValidConnection={handleIsValidConnection}
          fitView
          fitViewOptions={{ padding: 0.15, maxZoom: 1 }}
          proOptions={{ hideAttribution: true }}
          nodesDraggable={false}
          nodesConnectable={true}
        >
          <Background variant={BackgroundVariant.Dots} gap={20} size={1} color="#e5e7eb" />
        </ReactFlow>
      </div>
      <RelationshipDetailPanel selectedNodeId={selectedNodeId} />
    </div>
  )
}

// ---------------------------------------------------------------------------
// DataRelationshipGraph — public export with own ReactFlowProvider
// ---------------------------------------------------------------------------

export function DataRelationshipGraph() {
  return (
    <ReactFlowProvider>
      <DataRelationshipGraphInner />
    </ReactFlowProvider>
  )
}
